import React from 'react'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { LocationDescriptor } from 'history'
import { Button } from '@/components/atoms'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { TMiwaCardKeyReservation } from '@/models/miwa-lock'
import { TReadCard } from '@/utils/miwa-lock'
import { centeredModalStyle, modalFooterRightStyle } from './card-key-modal-style'

type Props = {
  card: TReadCard
  // 紐づく部屋割が見つからない場合は null（カードの情報だけを表示する）
  reservation: TMiwaCardKeyReservation | null
  onClose: () => void
  // 予約のステータスから詳細画面を決められない場合は null（予約詳細ボタンを出さない）
  reservationDetailLocation: LocationDescriptor | null
}

export const CardKeyInfoModal: React.FC<Props> = ({ card, reservation, onClose, reservationDetailLocation }) => {
  const { t } = useTranslation()

  // カードに時刻が書き込まれていない場合は日付だけを表示する
  const formatCardDateTime = (value: string): string =>
    value.includes(' ') ? dayjs(value).format(`${t('MM-DD')} HH:mm`) : dayjs(value).format(t('MM-DD'))

  return (
    <Modal customCss={centeredModalStyle(600)}>
      <ModalHeader>{t('Card information')}</ModalHeader>
      <ModalBody>
        <div css={infoBlockStyle}>
          <div css={itemStyle}>
            <p css={labelStyle}>{t('Room number')}</p>
            <p css={valueStyle}>{card.roomNumber}</p>
          </div>
          <div css={itemStyle}>
            <p css={labelStyle}>{t('Validity period')}</p>
            <p css={valueStyle}>
              {formatCardDateTime(card.startAt)} ~ {formatCardDateTime(card.endAt)}
            </p>
          </div>
          <div css={itemStyle}>
            <p css={labelStyle}>{t('Guest name')}</p>
            {/* 紐づく部屋割が見つからない・宿泊者名が未登録のときも項目は出し、取得できなかったことを示す */}
            <p css={valueStyle}>{reservation?.guestName?.trim() ? reservation.guestName : '-'}</p>
          </div>
          {reservation?.hasUnpaidSales && (
            <div css={unpaidStyle}>
              <img src={require('@/static/images/room/settle_orange.svg')} width={11} alt="" />
              <p>{t('Unpaid balance')}</p>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div css={modalFooterRightStyle}>
          <Button width={110} height={38} buttonType={3} onClick={onClose}>
            {t('Close')}
          </Button>
          {reservationDetailLocation && (
            // 新しいタブでも開けるようリンクにする。修飾キー付き・中クリックは別タブで開くため、元の画面のモーダルは閉じない
            <Link
              to={reservationDetailLocation}
              css={reservationDetailLinkStyle}
              onClick={e => {
                if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) onClose()
              }}
            >
              {t('Reservation details')}
            </Link>
          )}
        </div>
      </ModalFooter>
    </Modal>
  )
}

const infoBlockStyle = css({
  backgroundColor: '#fff',
  padding: '24px 20px',
})

const itemStyle = css({
  ':not(:last-of-type)': { marginBottom: 16 },
})

const labelStyle = css({
  color: '#676767',
  fontSize: 12,
  fontWeight: 'bold',
  marginBottom: 4,
})

const valueStyle = css({
  color: '#272727',
  fontSize: 18,
  fontWeight: 'bold',
})

// Button（buttonType=1）と同じ見た目のリンク
const reservationDetailLinkStyle = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 110,
  height: 38,
  marginLeft: 16,
  borderRadius: 24,
  background: '#F2A40B',
  color: '#fff',
  fontSize: 14,
  fontWeight: 'bold',
  textDecoration: 'none',
  '&:hover': {
    background: '#D68E0F',
  },
})

const unpaidStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  color: '#F2A40B',
  fontSize: 12,
  fontWeight: 'bold',
})
